import { Order } from "@/types/ApiTypes";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import DriverOrderCard from "./DriverOrderCard";
import toast from "react-hot-toast";
import ErrorMessage from "@/components/ErrorMessage";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { App } from "@capacitor/app";
import { Geolocation } from "@capacitor/geolocation"; // Import Capacitor Geolocation
import { useUserRole } from "@/hooks/useUserRole";
import { isWeb } from "@/utils/helpers";

export type ColumnId = "finished" | "delivering" | "delivered";

export default function DriverOrdersBoard() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
const { isDriver, openAppReviewBasedOnRole } = useUserRole();

  const baseUrl = import.meta.env.VITE_BASE_URL;
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );

  const base_socket = import.meta.env.VITE_SOCKET;
  const manuallyClosed = useRef(false);
  const trackingWsRef = useRef<WebSocket | null>(null);
  const trackingReconnectAttempts = useRef(0);
  const maxTrackingReconnectAttempts = 5;
  const watchIdRef = useRef<string | number | null>(null); // Update type to support Capacitor's watch ID (string)

  const deliveringOrders = orders.filter((order) => order.progress === "delivering");



 useEffect(() => {
  const cleanup = () => {
    if (watchIdRef.current) {
      if (isWeb) {
        navigator.geolocation.clearWatch(watchIdRef.current as number);
      } else {
        Geolocation.clearWatch({ id: watchIdRef.current as string });
      }
      watchIdRef.current = null;
    }
    if (trackingWsRef.current) {
      trackingWsRef.current.close();
      trackingWsRef.current = null;
    }
    trackingReconnectAttempts.current = 0;
  };

  if (!token || !base_socket || deliveringOrders.length === 0) {
    cleanup();
    return;
  }

  const connectTrackingSocket = () => {
    if (trackingWsRef.current?.readyState === WebSocket.OPEN) return;

    trackingWsRef.current = new WebSocket(
      `${base_socket}/socket/dashboard/delivery-service/live-tracking`,
    );

    trackingWsRef.current.onopen = () => {
      trackingReconnectAttempts.current = 0;
      trackingWsRef.current?.send(
        JSON.stringify({
          type: "authorize",
          token,
        }),
      );

      const watchPosition = isWeb
        ? () =>
            navigator.geolocation.watchPosition(
              (position) => {
                if (trackingWsRef.current?.readyState === WebSocket.OPEN) {
                  trackingWsRef.current.send(
                    JSON.stringify({
                      type: "update-position",
                      lat: position.coords.latitude,
                      lng: position.coords.longitude,
                    }),
                  );
                }
              },
              (error) => console.error("Geolocation error:", error),
              { enableHighAccuracy: true },
            )
        : async () => {
            try {
              return await Geolocation.watchPosition(
                { enableHighAccuracy: true },
                (position, error) => {
                  if (error) {
                    console.error("Geolocation error:", error);
                    return;
                  }
                  if (
                    position &&
                    trackingWsRef.current?.readyState === WebSocket.OPEN
                  ) {
                    trackingWsRef.current.send(
                      JSON.stringify({
                        type: "update-position",
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                      }),
                    );
                  }
                },
              );
            } catch (error) {
              console.error("Geolocation error:", error);
              return null;
            }
          };

      const result = watchPosition();
      if (isWeb) {
        // For web, result is a number
        watchIdRef.current = result as number;
      } else {
        // For mobile, result is a Promise<string | null>
        (result as Promise<string | null>).then((watchId) => {
          if (watchId) watchIdRef.current = watchId;
        });
      }
    };

    trackingWsRef.current.onclose = () => {
      cleanup();
      if (
        deliveringOrders.length > 0 &&
        trackingReconnectAttempts.current < maxTrackingReconnectAttempts
      ) {
        trackingReconnectAttempts.current += 1;
        setTimeout(connectTrackingSocket, 5000);
      }
    };

    trackingWsRef.current.onerror = (error) => {
      console.error("Tracking WebSocket error:", error);
      cleanup();
    };
  };

  connectTrackingSocket();

  return cleanup;
}, [token, base_socket, deliveringOrders]);

  useEffect(() => {
    if (!token || !base_socket) return;

    let socket: WebSocket | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    let heartbeatInterval: ReturnType<typeof setInterval> | null = null;

    manuallyClosed.current = false;

    const connectWebSocket = () => {
      if (socket && socket.readyState <= 1) return;

      toast.loading(t("driver.toast.connecting"), { id: "chef-ws" });

      socket = new WebSocket(`${base_socket}/socket/dashboard/orders`);

      socket.addEventListener("open", () => {
        toast.success(t("driver.toast.connected"), { id: "chef-ws" });
        socket?.send(JSON.stringify({ type: "authorize", token }));

        heartbeatInterval = setInterval(() => {
          if (socket?.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: "ping" }));
          }
        }, 15000);
      });

      socket.addEventListener("message", (event) => {
        try {
          const data = JSON.parse(event.data);
          const order = data.order;
          if (!order) return;

          updateOrder(order);

          if (order.status === "cancelled") {
            toast.error(t("chef.toast.cancelled", { id: order.orderId }), {
              id: `order-${order.orderId}`,
            });
          } else if (order.status === "returned") {
            toast.error(t("chef.toast.returned", { id: order.orderId }), {
              id: `order-${order.orderId}`,
            });
          } else {
            toast.success(t("chef.toast.newUpdate", { id: order.orderId }), {
              id: `order-${order.orderId}`,
            });
          }
        } catch {
          // Silently ignore invalid messages
        }
      });

      socket.addEventListener("close", () => {
        if (heartbeatInterval) clearInterval(heartbeatInterval);
        heartbeatInterval = null;

        if (!manuallyClosed.current) {
          toast.error(t("chef.toast.disconnected"), { id: "chef-ws" });
          getOrders();
          reconnectTimeout = setTimeout(connectWebSocket, 5000);
        }
      });

      socket.addEventListener("error", () => {
        toast.error(t("chef.toast.error"), { id: "chef-ws" });
        try {
          socket?.close();
          getOrders();
        } catch {
          // Ignore
        }
      });
    };

    connectWebSocket();

    const handleVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        (!socket || socket.readyState > 1) &&
        !manuallyClosed.current
      ) {
        toast(t("chef.toast.visible"), { id: "chef-ws" });
        connectWebSocket();
      }
    };

    const handleOnline = () => {
      if (!manuallyClosed.current && (!socket || socket.readyState > 1)) {
        toast(t("chef.toast.online"), { id: "chef-ws" });
        connectWebSocket();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("online", handleOnline);

    return () => {
      manuallyClosed.current = true;
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("online", handleOnline);

      toast.dismiss("chef-ws");

      if (heartbeatInterval) clearInterval(heartbeatInterval);
      if (reconnectTimeout) clearTimeout(reconnectTimeout);

      if (
        socket?.readyState === WebSocket.OPEN ||
        socket?.readyState === WebSocket.CONNECTING
      ) {
        socket.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, base_socket, t]);

  const updateOrder = (incomingOrder: Order) => {
    setOrders((prev) => {
      // Remove the incoming order if it exists
      const filtered = prev.filter((order) => order._id !== incomingOrder._id);

      // Ignore cancelled or returned-without-progress orders
      if (
        incomingOrder.status === "cancelled" ||
        (incomingOrder.status === "returned" && incomingOrder.progress === null)
      ) {
        return filtered;
      }

      // Add the updated order to the beginning
      return [incomingOrder, ...filtered];
    });
  };

  async function getOrders() {
    try {
      setIsLoading(true);
      setError(null);

      const response = await axios.get(
        `${baseUrl}/api/dashboard/orders?locale=${lang}`,
        {
          headers: {
            Authorization: token,
          },
        },
      );

      if (response.data.success) {
        setOrders(response.data.orders);
      } else {
        toast.error(response.data.error);
        setError(response.data.error);
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const ErrorMsg =
          error.response?.data?.error ||
          (t("userSettings.unexpectedError") as string);
        setError(ErrorMsg);
        toast.error(ErrorMsg);
      } else {
        setError(t("userSettings.unexpectedError") as string);
        toast.error(t("userSettings.unexpectedError") as string);
      }
    } finally {
      setIsLoading(false);
    }
  }

  const [shouldRefetch, setShouldRefetch] = useState(false);
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setShouldRefetch(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    let removeAppListener: (() => void) | undefined;

    App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        setShouldRefetch(true);
      }
    }).then((listener) => {
      removeAppListener = listener.remove;
    });

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (removeAppListener) removeAppListener();
    };
  }, []);

  useEffect(() => {
    if (!token) return;
    getOrders();
    setShouldRefetch(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, lang, t, token, shouldRefetch]);

  async function updateOrderProgress(id: string, type: string, progress: ColumnId) {
    const loader = toast.loading(t("ordersBody.OrderProgressUpdating"));

    try {
      let lat = 0;
      let lng = 0;

      if (progress === "delivering") {
        const position = isWeb
          ? await new Promise<GeolocationPosition>((resolve, reject) =>
              navigator.geolocation.getCurrentPosition(
                resolve,
                reject,
                {
                  enableHighAccuracy: true,
                  timeout: 5000,
                  maximumAge: 1000,
                },
              ),
            )
          : await Geolocation.getCurrentPosition({
              enableHighAccuracy: true,
              timeout: 5000,
            });

        lat = position.coords.latitude;
        lng = position.coords.longitude;
      }

      const options = {
        url: `${baseUrl}/api/dashboard/orders/${id}/update?locale=${lang}`,
        method: "PUT",
        headers: {
          Authorization: token,
        },
        data: {
          progress,
          type,
          lat: progress === "delivering" ? lat : undefined,
          lng: progress === "delivering" ? lng : undefined,
        },
      };

      const response = await axios.request(options);

      if (response.data.success) {
        toast.success(t("ordersBody.OrderProgressUpdatedSuccessfully"));
        return true;
      } else {
        getOrders();
        throw response.data.error;
      }
    } catch (error) {
      toast.error(t("ordersBody.OrderProgressUpdateFailed"));
      getOrders();
      console.error(error);
      return false;
    } finally {
      toast.dismiss(loader);
    }
  }

  const handleStatusChange = async (orderId: string, newStatus: ColumnId) => {
    const order = orders.find((o) => o._id === orderId);
    if (!order) return;

    const currentStatus = order.progress;

    if (
      (currentStatus === "delivered" && newStatus !== "delivered") ||
      (currentStatus === "delivering" && newStatus === "finished")
    ) {
      toast.error(t("driver.cannot_return_to_previous_status") as string);
      return;
    }

    const success = await updateOrderProgress(orderId, order.orderType, newStatus);

    if (success) {
      setOrders((prev) =>
        prev.map((o) =>
          o._id === orderId ? { ...o, progress: newStatus } : o,
        ),
      );
      if (newStatus === "delivered" && isDriver) {
        openAppReviewBasedOnRole();
      }
    }
  };

  if (isLoading)
    return (
      <>
        <Skeleton className="my-btn mb-5 me-10 ms-auto block h-[30px] w-[200px] px-5 py-1 dark:bg-slate-600 dark:bg-opacity-35" />
        <Skeleton className="mx-auto mt-6 h-[100px] rounded-lg p-3 shadow-sm dark:bg-slate-600 dark:bg-opacity-35"></Skeleton>
        <div className="mt-5 flex flex-wrap gap-4 p-4">
          {[...Array(6)].map((_, i) => (
            <Skeleton
              key={i}
              className="h-[200px] w-full max-w-[400px] rounded-lg dark:bg-slate-600 dark:bg-opacity-35"
            />
          ))}
        </div>
      </>
    );

  if (error) return <ErrorMessage msg={error} />;
  // First filter out delivered orders
  const activeOrders = orders.filter((order) => order.progress !== "delivered");

  // Then check for empty state
  if (!activeOrders.length) {
    return (
      <>
        <Link
          to={"/driver-orders-history"}
          className="my-btn mb-5 me-10 ms-auto block w-fit px-5 py-1"
        >
          {t("driver.history")}
        </Link>

        <Alert className="border-none bg-skyblue text-sidebar dark:border-none dark:bg-skyblue dark:text-sidebar">
          <AlertTitle className="text-center text-xl">
            {t("driver.noOrdersFound")}
          </AlertTitle>
          <AlertDescription className="my-5 text-center font-semibold">
            {t("driver.noOrdersMessage")}
          </AlertDescription>
        </Alert>
      </>
    );
  }

  return (
    <main className="mt-5 lg:mt-0">
      <Link
        to={"/driver-orders-history"}
        className="my-btn mb-5 ms-auto block w-fit px-5 py-1"
      >
        {t("driver.history")}
      </Link>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {orders
          .filter((order) => order.progress !== "delivered") // Filter out delivered orders
          .map((order) => (
            <DriverOrderCard
              key={order._id}
              order={order}
              columnId={order.progress as ColumnId}
              onStatusChange={handleStatusChange}
            />
          ))}
      </div>
    </main>
  );
}